import { type PhraseRow } from '@/pages/report/utils/comparisons-data-processor';
import { type SeoList } from '@/pages/report/utils/report-table-data-processor';
import { createSlice } from '@reduxjs/toolkit';

export type SeoListExtended = SeoList & {
  foundPhrasesCount: number | null;
  totalPhrasesCount: number | null;
};

export interface ShelvesData {
  checkedPhraseIds: PhraseRow['id'][];
  listId: SeoList['id'];
  topRadioFilter: string;
  mainRadioFilter: string;
  comparisonsRadioFilter: string;
  lists: SeoListExtended[];
  fetchedProductIds: string[];
  fetchedByDate: Record<string, string[]>; 
}

const initialState: ShelvesData = {
  checkedPhraseIds: [],
  listId: 'all',
  topRadioFilter: 'all',
  mainRadioFilter: 'all',
  comparisonsRadioFilter: 'all',
  lists: [],
  fetchedProductIds: [],
  fetchedByDate: {},
};

export interface Action<P> {
  type: string;
  payload: P;
}

type FetchedByDatePayload = { date: string; ids: string[] };

export const shelvesTabDataSlice = createSlice({
  name: 'shelvesTabDataSlice',
  initialState,
  reducers: {
    setCheckedPhraseIds: (state, action: Action<string[]>) => ({
      ...state,
      checkedPhraseIds: action.payload,
    }),
    setListId: (state, action: Action<string>) => ({
      ...state,
      listId: action.payload,
    }),
    setTopRadioFilter: (state, action: Action<string>) => ({
      ...state,
      topRadioFilter: action.payload,
    }),
    setMainRadioFilter: (state, action: Action<string>) => ({
      ...state,
      mainRadioFilter: action.payload,
    }),
    setComparisonsRadioFilter: (state, action: Action<string>) => ({
      ...state,
      comparisonsRadioFilter: action.payload,
    }),
    setLists: (state, action: Action<SeoListExtended[]>) => ({
      ...state,
      lists: action.payload,
    }),
    addFetchedProductIds: (state, action: Action<string[]>) => {
      const newIds = action.payload.filter((id) => !state.fetchedProductIds.includes(id));

      return {
        ...state,
        fetchedProductIds: [...state.fetchedProductIds, ...newIds],
      };
    },
    resetFetchedProductIds: (state) => ({
      ...state,
      fetchedProductIds: [],
    }),
    addFetchedProductIdsByDate: (state, action: Action<FetchedByDatePayload>) => {
      const { date, ids } = action.payload;
      const prev = new Set(state.fetchedByDate[date] ?? []);
      ids.forEach((id) => {
        prev.add(String(id))
      });

      return {
        ...state,
        fetchedByDate: {
          ...state.fetchedByDate,
          [date]: Array.from(prev),
        },
      };
    },
    reset: () => ({ ...initialState }),
  },
});

export const {
  setCheckedPhraseIds,
  setListId,
  setTopRadioFilter,
  setMainRadioFilter,
  setComparisonsRadioFilter,
  setLists,
  addFetchedProductIds,
  resetFetchedProductIds,
  reset,
  addFetchedProductIdsByDate
} = shelvesTabDataSlice.actions;

export default shelvesTabDataSlice.reducer;
